import { THRESHOLD } from "./motion-tokens.js";

/**
 * THE THRESHOLD'S DISSOLVE (2026-11-04; the handoff of 2026-11-03, "Transition
 * between coach and story"; the plan's release 3). A crossing between the
 * study and the book is a 620ms dissolve: the outgoing page fades over the
 * first half, the incoming arrives over the second, and a quiet neutral
 * backdrop interpolates between the two desks beneath them (the root ramps
 * and the backdrop are the stylesheet's, under `html[data-crossing]`). Near
 * the middle, three things ride on the browser's view transition as live
 * named groups above the two pages: a warm veil, one deterministic grain
 * field split into its red, green and blue copies and drawn at small
 * offsets that wiggle and converge, and a fray of the two separators the
 * page actually has (the header rule and the pane divider). No flare, no
 * halo, no shake of the page: the character is the small colour
 * disagreements in the grain, and at 0% and 100% every effect is zero.
 *
 * Code owns the clock and the drawing; nothing here decides where the
 * learner goes. The pure parts (`smooth`, `ramp`, `seeded`, `grainField`,
 * `envelopes`, `channelOffsets`, `thresholdEdges`) are exported for their
 * tests; `beginCrossing` is what `place.ts` calls, once per crossing, with
 * the controller's two moments: before the browser captures the outgoing
 * page, and inside the update callback once the destination is rendered.
 * A newer crossing cancels the previous controller first; the clock starts
 * when the transition is ready, a 620ms timer takes the overlays away, the
 * attribute waits for the transition to finish (or for the timer, where a
 * stand-in gives no promise); nothing runs while the page is idle.
 */

/** `x·x·(3 − 2x)` on `[0, 1]`: the brief's `smooth`, which `cubic-bezier(1/3, 0, 2/3, 1)` is exactly. */
export function smooth(x: number): number {
  const c = x < 0 ? 0 : x > 1 ? 1 : x;
  return c * c * (3 - 2 * c);
}

/** The brief's `ramp(t, a, b)`: 0 before `a`, 1 after `b`, smooth between. */
export function ramp(t: number, a: number, b: number): number {
  return smooth((t - a) / (b - a));
}

/** The brief's seeded generator (mulberry32), so the field is the same on every machine. */
export function seeded(seed: number): () => number {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let v = Math.imul(a ^ (a >>> 15), 1 | a);
    v = (v + Math.imul(v ^ (v >>> 7), 61 | v)) ^ v;
    return ((v ^ (v >>> 14)) >>> 0) / 4294967296;
  };
}

/** One monochrome grain field, one sample per CSS pixel plus the padding on every side, from three uniform draws per sample. */
export interface GrainField {
  readonly width: number;
  readonly height: number;
  readonly gray: Uint8ClampedArray;
}

export function grainField(width: number, height: number, seed: number = THRESHOLD.grain.seed): GrainField {
  const pad = THRESHOLD.grain.paddingPx;
  const w = Math.max(1, Math.round(width)) + 2 * pad;
  const h = Math.max(1, Math.round(height)) + 2 * pad;
  const next = seeded(seed);
  const gray = new Uint8ClampedArray(w * h);
  for (let k = 0; k < gray.length; k++) {
    const n = (next() + next() + next()) / 3 - 0.5;
    const magnitude = Math.pow(Math.abs(2 * n), THRESHOLD.grain.exponent) * 127.5;
    gray[k] = Math.round(127.5 + Math.sign(n) * magnitude);
  }
  return { width: w, height: h, gray };
}

/** The two envelopes: `E` for the chromatic offsets and the edges, `G` for the grain and the veil. */
export function envelopes(t: number): { readonly E: number; readonly G: number } {
  const c = THRESHOLD.chromatic;
  const g = THRESHOLD.grain;
  return {
    E: ramp(t, c.attack.from, c.attack.to) * (1 - ramp(t, c.release.from, c.release.to)),
    G: ramp(t, g.attack.from, g.attack.to) * (1 - ramp(t, g.release.from, g.release.to)),
  };
}

/** Which way the crossing goes: into the book (the brief's entry) or back to the study (its exit). */
export type Direction = "to-book" | "to-study";

const PHASES: readonly [number, number, number] = [0, (2 * Math.PI) / 3, (4 * Math.PI) / 3];

/** Where channel `i` (0 red, 1 green, 2 blue) is drawn at `t`, in CSS pixels; zero at both ends, since `E` is. */
export function channelOffsets(t: number, i: 0 | 1 | 2, direction: Direction): { readonly dx: number; readonly dy: number } {
  const { E } = envelopes(t);
  const u = direction === "to-book" ? t : 1 - t;
  const phase = PHASES[i];
  const base = (1 - i) * E * THRESHOLD.rgb.separationPx;
  const amp = E * THRESHOLD.rgb.wigglePx;
  const r = THRESHOLD.rgb;
  return {
    dx: base + amp * Math.sin(2 * Math.PI * r.xCycles * u + phase),
    dy: r.yBase * base + r.yWiggle * amp * Math.sin(2 * Math.PI * r.yCycles * u + phase + r.yPhase),
  };
}

/** A separator's place on the page, in CSS pixels; `vertical` for the pane divider when the panes sit side by side. */
export interface Edge {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly vertical: boolean;
}

/**
 * The two separators the brief frays, found from the layout as it stands:
 * the header rule is the bottom pixel of the site header, the pane divider
 * is the conversation panel's right border, or its bottom border where the
 * panes stack. Null where the page has no such separator.
 */
export function thresholdEdges(doc: Document): { readonly rule: Edge | null; readonly divider: Edge | null } {
  const view = doc.defaultView;
  const header = doc.querySelector('[data-region="site-header"]');
  const rule: Edge | null = header === null ? null : ((r) => (r.width > 0 && r.height > 0 ? { x: r.left, y: r.bottom - 1, width: r.width, height: 1, vertical: false } : null))(header.getBoundingClientRect());
  const panel = doc.querySelector(".pcs-narration");
  let divider: Edge | null = null;
  if (panel !== null && view !== null) {
    const style = view.getComputedStyle(panel);
    const right = Number.parseFloat(style.borderRightWidth);
    const bottom = Number.parseFloat(style.borderBottomWidth);
    const r = panel.getBoundingClientRect();
    if (right > 0 && r.height > 0) divider = { x: r.right - right, y: r.top, width: right, height: r.height, vertical: true };
    else if (bottom > 0 && r.width > 0) divider = { x: r.left, y: r.bottom - bottom, width: r.width, height: bottom, vertical: false };
  }
  return { rule, divider };
}

/* ------------------------------------------------------------------------ */
/* The drawing. Everything below touches the document and the clock.         */
/* ------------------------------------------------------------------------ */

export const CROSSING_ATTRIBUTE = "data-crossing";
const VEIL = "pc-threshold-veil";
const GRAIN = "pc-threshold-grain";
const EDGES = "pc-threshold-edges";

interface CachedField {
  readonly key: string;
  readonly field: GrainField;
}

let fieldCache: CachedField | null = null;

function fieldKey(width: number, height: number): string {
  return `${String(Math.round(width))}x${String(Math.round(height))}`;
}

/** The viewport's grain field, one byte per sample, built once per viewport size and kept (about two megabytes); the channel canvases are made from it for each crossing and released after. */
function fieldFor(width: number, height: number): GrainField {
  const key = fieldKey(width, height);
  if (fieldCache !== null && fieldCache.key === key) return fieldCache.field;
  const field = grainField(width, height);
  fieldCache = { key, field };
  return field;
}

type Channels = readonly [HTMLCanvasElement, HTMLCanvasElement, HTMLCanvasElement];

/** The field as its red, green and blue copies; null where canvases cannot be drawn (jsdom has no 2D context at all). */
function channelsFor(doc: Document, field: GrainField): Channels | null {
  if (typeof doc.defaultView?.CanvasRenderingContext2D !== "function") return null;
  const made: HTMLCanvasElement[] = [];
  for (let channel = 0; channel < 3; channel++) {
    const canvas = doc.createElement("canvas");
    canvas.width = field.width;
    canvas.height = field.height;
    const ctx = canvas.getContext("2d");
    if (ctx === null) return null;
    const image = ctx.createImageData(field.width, field.height);
    const data = image.data;
    for (let k = 0; k < field.gray.length; k++) {
      data[k * 4 + channel] = field.gray[k] ?? 0;
      data[k * 4 + 3] = 255;
    }
    ctx.putImageData(image, 0, 0);
    made.push(canvas);
  }
  const [r, g, b] = made;
  return r === undefined || g === undefined || b === undefined ? null : [r, g, b];
}

/**
 * Builds the grain field for the current viewport ahead of a crossing, on
 * idle time, and again when the viewport settles at a new size, so a
 * crossing never waits for it. Returns the function that stops listening.
 */
export function warmThreshold(doc: Document = globalThis.document): () => void {
  const view = doc.defaultView;
  if (view === null) return () => undefined;
  const idleView = view as Window & { requestIdleCallback?: (cb: () => void) => number; cancelIdleCallback?: (id: number) => void };
  let idle = 0;
  const build = (): void => { idle = 0; fieldFor(view.innerWidth, view.innerHeight); };
  const schedule = (): void => {
    if (idle !== 0) return;
    if (typeof idleView.requestIdleCallback === "function") idle = idleView.requestIdleCallback(build);
    else idle = view.setTimeout(build, 1);
  };
  let settle = 0;
  const onResize = (): void => {
    view.clearTimeout(settle);
    settle = view.setTimeout(schedule, 300);
  };
  schedule();
  view.addEventListener("resize", onResize);
  return () => {
    view.removeEventListener("resize", onResize);
    view.clearTimeout(settle);
    if (idle !== 0) {
      if (typeof idleView.cancelIdleCallback === "function") idleView.cancelIdleCallback(idle);
      else view.clearTimeout(idle);
    }
  };
}

/** For tests: forget the built grain. */
export function resetThresholdCache(): void {
  fieldCache = null;
}

function overlay(doc: Document, name: string): HTMLDivElement {
  const el = doc.createElement("div");
  el.className = name;
  el.setAttribute("aria-hidden", "true");
  el.style.position = "fixed";
  el.style.inset = "0";
  el.style.pointerEvents = "none";
  el.style.setProperty("view-transition-name", name);
  return el;
}

function edgeElement(doc: Document, edge: Edge, kind: "divider" | "rule"): HTMLDivElement {
  const el = doc.createElement("div");
  el.className = `${EDGES}-${kind}`;
  el.style.position = "absolute";
  el.style.left = `${String(edge.x)}px`;
  el.style.top = `${String(edge.y)}px`;
  el.style.width = `${String(edge.width)}px`;
  el.style.height = `${String(edge.height)}px`;
  el.style.opacity = "0";
  if (kind === "divider") {
    el.style.background = "var(--pc-ice)";
    el.style.boxShadow = edge.vertical
      ? "1px 0 0 var(--pc-threshold-rose), -1px 0 0 var(--pc-ice)"
      : "0 1px 0 var(--pc-threshold-rose), 0 -1px 0 var(--pc-ice)";
  } else {
    const c = THRESHOLD.headerRule;
    el.style.background = `linear-gradient(90deg, transparent, var(--pc-ice) ${String(c.cyanStop * 100)}%, var(--pc-threshold-rose) ${String(c.roseStop * 100)}%, transparent)`;
  }
  return el;
}

/** What a started transition hands back, as far as the controller reads it. */
interface TransitionLike {
  ready?: unknown;
  finished?: unknown;
}

/** One crossing's controller, from before the capture to the end of the run. */
export interface Crossing {
  /** Inside the update callback, once the destination is rendered: mounts the three overlays at their zero state, so the browser captures them as live named groups. */
  mount(): void;
  /**
   * After `startViewTransition` has returned: starts the clock when the
   * transition is ready (its pseudo-tree built, its animations about to
   * run), so the overlays' envelopes and the root ramps share one `t`;
   * cancels instead if the transition is skipped; and keeps `data-crossing`
   * until the transition has finished, however long the browser takes to
   * render the destination. Without a transition object (a stand-in in a
   * test) the clock starts at once and a timer ends everything.
   */
  start(transition?: unknown): void;
  /** Removes everything at once, whichever moment the run is at. */
  cancel(): void;
}

let current: Crossing | null = null;

/** The crossing under way, if any; a newer one replaces it. */
export function currentCrossing(): Crossing | null {
  return current;
}

/**
 * Begins a crossing: cancels the previous one, writes `data-crossing` on the
 * root so the outgoing page's local animations stop and the root ramps
 * apply to the capture about to be taken, takes the grain field (built on
 * idle time by `warmThreshold`, or now) as its three channel copies, and
 * remembers where the outgoing page's separators are. Called before
 * `startViewTransition`.
 */
export function beginCrossing(direction: Direction, doc: Document = globalThis.document): Crossing {
  current?.cancel();
  const root = doc.documentElement;
  const view = doc.defaultView;
  root.setAttribute(CROSSING_ATTRIBUTE, direction);
  const outgoing = thresholdEdges(doc);
  let channels: Channels | null = view === null ? null : channelsFor(doc, fieldFor(view.innerWidth, view.innerHeight));

  let frame = 0;
  let timer = 0;
  let mounted: readonly HTMLElement[] = [];
  let done = false;
  let waitsForFinish = false;
  let onStill: (() => void) | null = null;
  let stillQuery: MediaQueryList | null = null;
  let draw: ((t: number) => void) | null = null;

  const unmount = (): void => {
    for (const el of mounted) el.remove();
    mounted = [];
    // The channel canvases are this crossing's alone: the field stays cached, they do not.
    channels = null;
  };
  const cancel = (): void => {
    if (done) return;
    done = true;
    if (view !== null) {
      if (frame !== 0) view.cancelAnimationFrame(frame);
      if (timer !== 0) view.clearTimeout(timer);
    }
    if (stillQuery !== null && onStill !== null) stillQuery.removeEventListener?.("change", onStill);
    unmount();
    if (current === crossing) current = null;
    // The attribute goes one frame after the overlays, so the last frame drawn never falls back to the unscoped fade.
    const clear = (): void => { if (root.getAttribute(CROSSING_ATTRIBUTE) === direction && current === null) root.removeAttribute(CROSSING_ATTRIBUTE); };
    if (view !== null) view.requestAnimationFrame(clear);
    else clear();
  };

  const mount = (): void => {
    if (done || view === null) return;
    const destination = thresholdEdges(doc);
    const rule = destination.rule ?? outgoing.rule;
    const divider = destination.divider ?? outgoing.divider;
    const dpr = Math.min(THRESHOLD.grain.maxDpr, Math.max(1, view.devicePixelRatio || 1));

    const veil = overlay(doc, VEIL);
    veil.style.background = "var(--pc-threshold-veil)";
    veil.style.opacity = "0";

    const grainBox = overlay(doc, GRAIN);
    let ctx: CanvasRenderingContext2D | null = null;
    let canvas: HTMLCanvasElement | null = null;
    const field = channels === null ? null : { width: channels[0].width, height: channels[0].height };
    if (field !== null) {
      canvas = doc.createElement("canvas");
      canvas.width = Math.round(field.width * dpr);
      canvas.height = Math.round(field.height * dpr);
      canvas.style.position = "absolute";
      canvas.style.left = `${String(-THRESHOLD.grain.paddingPx)}px`;
      canvas.style.top = `${String(-THRESHOLD.grain.paddingPx)}px`;
      canvas.style.width = `${String(field.width)}px`;
      canvas.style.height = `${String(field.height)}px`;
      ctx = canvas.getContext("2d");
      if (ctx !== null) {
        ctx.imageSmoothingEnabled = true;
        grainBox.appendChild(canvas);
      }
    }
    grainBox.style.opacity = "0";

    const edges = overlay(doc, EDGES);
    const dividerEl = divider === null ? null : edgeElement(doc, divider, "divider");
    const ruleEl = rule === null ? null : edgeElement(doc, rule, "rule");
    if (dividerEl !== null) edges.appendChild(dividerEl);
    if (ruleEl !== null) edges.appendChild(ruleEl);

    // The veil below the grain below the edges: tree order is paint order for the named groups.
    doc.body.append(veil, grainBox, edges);
    mounted = [veil, grainBox, edges];

    draw = (t: number): void => {
      const { E, G } = envelopes(t);
      veil.style.opacity = String(G * THRESHOLD.veil.peakOpacity);
      grainBox.style.opacity = String(G * THRESHOLD.grain.opacityFactor * THRESHOLD.strength);
      if (ctx !== null && canvas !== null && channels !== null && field !== null) {
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, field.width, field.height);
        ctx.globalCompositeOperation = "lighter";
        for (const i of [0, 1, 2] as const) {
          const { dx, dy } = channelOffsets(t, i, direction);
          ctx.drawImage(channels[i], dx, dy);
        }
      }
      if (dividerEl !== null && divider !== null) {
        dividerEl.style.opacity = String(E * THRESHOLD.divider.opacityFactor * THRESHOLD.strength);
        const shift = Math.sin(2 * Math.PI * t) * THRESHOLD.divider.shiftPx * THRESHOLD.strength;
        dividerEl.style.transform = divider.vertical ? `translateX(${String(shift)}px)` : `translateY(${String(shift)}px)`;
      }
      if (ruleEl !== null) ruleEl.style.opacity = String(E * THRESHOLD.headerRule.opacityFactor * THRESHOLD.strength);
    };
    draw(0);
  };

  /** The clock: from the moment the transition is ready, one frame loop for 620ms, then the overlays go. */
  const begin = (): void => {
    if (done || view === null || draw === null) return;
    const render = draw;
    const started = view.performance.now();
    const tick = (): void => {
      if (done) return;
      const t = Math.min(1, (view.performance.now() - started) / THRESHOLD.durationMs);
      render(t);
      if (t < 1) frame = view.requestAnimationFrame(tick);
      else frame = 0;
    };
    frame = view.requestAnimationFrame(tick);
    timer = view.setTimeout(() => {
      timer = 0;
      if (frame !== 0) view.cancelAnimationFrame(frame);
      frame = 0;
      if (waitsForFinish) unmount();
      else cancel();
    }, THRESHOLD.durationMs);

    // A reader who asks for stillness mid-run gets the end state at once.
    const query = view.matchMedia?.("(prefers-reduced-motion: reduce)") ?? null;
    if (query !== null && typeof query.addEventListener === "function") {
      stillQuery = query;
      onStill = () => { if (query.matches) cancel(); };
      query.addEventListener("change", onStill);
    }
  };

  const start = (transition?: unknown): void => {
    if (done) return;
    const t = transition !== null && typeof transition === "object" ? (transition as TransitionLike) : null;
    if (t !== null && t.finished instanceof Promise) {
      // The attribute stays until the browser has finished the ramps, however late they started; the overlays still go on the timer.
      waitsForFinish = true;
      t.finished.then(cancel, cancel);
    }
    if (t !== null && t.ready instanceof Promise) t.ready.then(begin, cancel);
    else begin();
  };

  const crossing: Crossing = { mount, start, cancel };
  current = crossing;
  return crossing;
}
